import { useEffect, useState, useCallback } from "react";

const TEAL = "#15aece";
const DARK = "#212121";

const StatusBadge = ({ success }) => (
  <span
    className="inline-block px-2 py-0.5 rounded text-xs font-medium"
    style={
      success
        ? { background: "#e6f7fb", color: TEAL }
        : { background: "#fef2f2", color: "#dc2626" }
    }
  >
    {success ? "OK" : "Failed"}
  </span>
);

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-sm p-5" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
    <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#888" }}>{label}</p>
    <p className="text-3xl font-bold" style={{ color: DARK }}>{value}</p>
  </div>
);

function OverviewTab({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const PAGE_SIZE = 20;
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [statsRes, logsRes] = await Promise.all([
        fetch("/admin/stats", { headers: authHeaders }),
        fetch(`/admin/audit?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`, { headers: authHeaders }),
      ]);

      if (statsRes.status === 401 || logsRes.status === 401) {
        onLogout();
        return;
      }

      setStats(await statsRes.json());
      const logsData = await logsRes.json();
      setLogs(logsData.data);
      setTotal(logsData.total);
    } catch {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formatTime = (iso) => new Date(iso).toLocaleString();

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-500">{error}</p>}

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Requests" value={stats.total} />
            <StatCard label="Last 24 Hours" value={stats.last24h} />
            <StatCard label="Last 7 Days" value={stats.last7d} />
            <StatCard label="Success Rate" value={`${stats.successRate}%`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-sm p-5" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
              <h2 className="text-sm font-semibold mb-4" style={{ color: DARK }}>Requests by User</h2>
              {stats.byUser.length === 0 && <p className="text-sm" style={{ color: "#aaa" }}>No data yet.</p>}
              <div className="space-y-3">
                {stats.byUser.map(([user, count]) => {
                  const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                  return (
                    <div key={user}>
                      <div className="flex justify-between text-xs mb-1" style={{ color: "#555" }}>
                        <span className="truncate max-w-[200px]">{user}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <div className="w-full rounded-full h-1.5" style={{ background: "#e5e7eb" }}>
                        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: TEAL }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-sm p-5" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
              <h2 className="text-sm font-semibold mb-4" style={{ color: DARK }}>Requests by Endpoint</h2>
              {stats.byEndpoint.length === 0 && <p className="text-sm" style={{ color: "#aaa" }}>No data yet.</p>}
              <div className="space-y-3">
                {stats.byEndpoint.map(([endpoint, count]) => {
                  const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                  return (
                    <div key={endpoint}>
                      <div className="flex justify-between text-xs mb-1" style={{ color: "#555" }}>
                        <span>{endpoint}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <div className="w-full rounded-full h-1.5" style={{ background: "#e5e7eb" }}>
                        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: "#1863dc" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="bg-white rounded-sm overflow-hidden" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #f0f0f0" }}>
          <h2 className="text-sm font-semibold" style={{ color: DARK }}>Audit Log</h2>
          <span className="text-xs" style={{ color: "#aaa" }}>{total} total entries</span>
        </div>

        {loading ? (
          <p className="text-sm p-5" style={{ color: "#aaa" }}>Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Time", "User", "Method", "Endpoint", "Keys", "Entity ID", "Status"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "#888" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm" style={{ color: "#aaa" }}>
                      No requests yet.
                    </td>
                  </tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderTop: "1px solid #f5f5f5" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: "#777" }}>{formatTime(log.timestamp)}</td>
                    <td className="px-4 py-3 max-w-[150px] truncate text-sm" style={{ color: DARK }}>{log.username}</td>
                    <td className="px-4 py-3">
                      <span
                        className="font-mono text-xs px-2 py-0.5 rounded"
                        style={
                          log.method === "GET"
                            ? { background: "#e6f7fb", color: TEAL }
                            : { background: "#fff7ed", color: "#ea580c" }
                        }
                      >
                        {log.method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: "#555" }}>{log.endpoint}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "#888" }}>{(log.keys || []).join(", ")}</td>
                    <td className="px-4 py-3 font-mono text-xs max-w-[120px] truncate" style={{ color: "#aaa" }}>{log.entityId}</td>
                    <td className="px-4 py-3"><StatusBadge success={log.success} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="px-5 py-3 flex items-center justify-between text-sm" style={{ borderTop: "1px solid #f0f0f0", color: "#777" }}>
            <span>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded text-sm disabled:opacity-40 transition-opacity"
                style={{ border: "1px solid #e5e7eb" }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              >
                Prev
              </button>
              <button
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded text-sm disabled:opacity-40 transition-opacity"
                style={{ border: "1px solid #e5e7eb" }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BpxTable({ title, tableData, filename }) {
  function handleDownload() {
    const lines = [tableData.headers.join('|'), ...tableData.rows.map(r => r.join('|'))];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-white rounded-sm" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        <h3 className="text-sm font-semibold" style={{ color: DARK }}>{title}</h3>
        <button
          onClick={handleDownload}
          className="text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: TEAL }}
        >
          Download
        </button>
      </div>
      {tableData.rows.length === 0 ? (
        <p className="text-sm p-5" style={{ color: "#aaa" }}>No data found.</p>
      ) : (
        <div style={{ overflow: "auto", maxHeight: "420px" }}>
          <table className="text-sm" style={{ minWidth: "max-content", width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr style={{ background: "#f9fafb" }}>
                {tableData.headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: "#888", borderBottom: "1px solid #f0f0f0" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, i) => (
                <tr
                  key={i}
                  style={{ borderTop: "1px solid #f5f5f5" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                >
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: "#555" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BpxFilesTab({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/admin/bpx-files", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: DARK }}>BPX Files</h2>
        <button onClick={fetchData} className="text-sm font-medium transition-opacity hover:opacity-70" style={{ color: TEAL }}>
          Refresh
        </button>
      </div>

      {loading && <p className="text-sm" style={{ color: "#aaa" }}>Loading from SFTP...</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && data && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <BpxTable title="Production" tableData={data.production} filename="BPX_production.txt" />
          <BpxTable title="Well Master" tableData={data.wellmaster} filename="BPX_well_master.txt" />
        </div>
      )}
    </div>
  );
}

function PlainsEmailTab({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/admin/plains-email", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatTime = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold" style={{ color: DARK }}>Plains Email Data</h2>
          {data?.lastUpdated && (
            <p className="text-xs mt-0.5" style={{ color: "#aaa" }}>Last loaded: {formatTime(data.lastUpdated)}</p>
          )}
        </div>
        <button onClick={fetchData} className="text-sm font-medium transition-opacity hover:opacity-70" style={{ color: TEAL }}>
          Refresh
        </button>
      </div>

      {loading && <p className="text-sm" style={{ color: "#aaa" }}>Loading...</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && data && (
        data.tables.length === 0
          ? <p className="text-sm" style={{ color: "#aaa" }}>No data cached yet — waiting for next cron trigger.</p>
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              {data.tables.map((t) => (
                <BpxTable key={t.mailbox} title={t.mailbox} tableData={t.data} filename={`Plains_${t.mailbox}.csv`} />
              ))}
            </div>
          )
      )}
    </div>
  );
}

const FORCE_SEND_PHRASE = "SEND TO CUSTOMERS";
const RED = "#dc2626";

const REPORT_LABELS = {
  genericReport: "Generic",
  tlmReport: "TLM",
  alarmReport: "Alarm",
  batchReport: "Batch",
};

const RESULT_FILTERS = [
  { id: "any", label: "Any last result" },
  { id: "failed", label: "Failed" },
  { id: "sent", label: "Sent" },
  { id: "skipped", label: "Skipped" },
  { id: "dry-run", label: "Dry run" },
  { id: "none", label: "Never run" },
];

const Pill = ({ children, bg, color, title }) => (
  <span
    title={title}
    className="inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
    style={{ background: bg, color }}
  >
    {children}
  </span>
);

const statusColors = (status) =>
  status === "sent"
    ? { bg: "#e6f7fb", color: TEAL }
    : status === "failed"
      ? { bg: "#fef2f2", color: "#dc2626" }
      : status === "dry-run"
        ? { bg: "#eef2ff", color: "#4f46e5" }
        : { bg: "#f3f4f6", color: "#6b7280" };

const ReportPill = ({ report }) => {
  const label = REPORT_LABELS[report.type] || report.type;

  if (!report.selected) {
    return <Pill bg="#f3f4f6" color="#9ca3af" title={`${report.key} is turned off`}>{label} off</Pill>;
  }
  if (!report.configured) {
    return <Pill bg="#fef2f2" color="#dc2626" title={`No server config for "${report.type}"`}>{label} {report.time} !</Pill>;
  }
  if (report.dueNow) {
    return <Pill bg="#fff7ed" color="#ea580c" title="Scheduled for the current 30-minute slot">{label} {report.time} due</Pill>;
  }
  return <Pill bg="#e6f7fb" color={TEAL} title={`${report.key} sends at ${report.time}`}>{label} {report.time}</Pill>;
};

// Everything the services attach to a thrown error: status codes inline, the
// response body and stack behind a toggle so a long failure stays readable.
function ErrorDetail({ error }) {
  const [open, setOpen] = useState(false);

  if (!error) return null;

  const codes = [
    ["HTTP", error.httpStatus],
    ["TB code", error.thingsboardErrorCode],
    ["code", error.code],
    ["SMTP", error.smtpResponseCode],
    ["cmd", error.smtpCommand],
    ["timed out", error.timedOut ? "yes" : null],
  ].filter(([, value]) => value !== null && value !== undefined);

  const body = [error.responseBody, error.smtpResponse, error.stack].filter(Boolean).join("\n\n");

  return (
    <div className="mt-1">
      {codes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {codes.map(([label, value]) => (
            <span key={label} className="px-1.5 py-0.5 rounded font-mono" style={{ background: "#fef2f2", color: "#b91c1c", fontSize: "10px" }}>
              {label} {value}
            </span>
          ))}
        </div>
      )}
      {body && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            className="font-medium"
            style={{ color: "#dc2626", background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "11px" }}
          >
            {open ? "Hide details" : "Show details"}
          </button>
          {open && (
            <pre
              className="mt-1 p-2 rounded"
              style={{ background: "#fff5f5", color: "#7f1d1d", fontSize: "10px", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "220px", overflowY: "auto" }}
            >
              {body}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

// Collapsible card. Sections stay mounted so table state survives a collapse.
function Panel({ title, subtitle, right, open, onToggle, children }) {
  return (
    <div className="bg-white rounded-sm overflow-hidden" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <div
        className="px-5 py-4 flex items-center justify-between gap-4"
        style={{ borderBottom: open ? "1px solid #f0f0f0" : "none" }}
      >
        <button
          onClick={onToggle}
          className="flex items-center gap-2 text-left"
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <span style={{ color: "#bbb", fontSize: "11px", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            &#9654;
          </span>
          <span>
            <span className="text-sm font-semibold" style={{ color: DARK }}>{title}</span>
            {subtitle && <span className="text-xs ml-2" style={{ color: "#aaa" }}>{subtitle}</span>}
          </span>
        </button>
        {open && right}
      </div>
      {open && children}
    </div>
  );
}

// Live view of whichever run is still open, with the stop control for tests.
function ActiveRunBar({ run, token, onChanged }) {
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");

  const { done = 0, total = 0, current } = run.progress || {};
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const stop = async () => {
    setStopping(true);
    setError("");
    try {
      const res = await fetch("/admin/csv-reports/test/stop", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      onChanged();
    } catch (err) {
      setError(err.message);
      setStopping(false);
    }
  };

  const stopped = run.cancelRequested || stopping;

  return (
    <div
      className="bg-white rounded-sm p-5"
      style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.07)", borderLeft: `3px solid ${stopped ? "#ea580c" : run.live ? "#dc2626" : TEAL}` }}
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <span className="text-sm font-semibold" style={{ color: DARK }}>
            {run.manual
              ? run.live
                ? "Force send in progress"
                : run.dryRunMode
                  ? "Dry run in progress"
                  : "Test run in progress"
              : "Scheduled run in progress"}
          </span>
          <span className="text-xs ml-2" style={{ color: "#888" }}>
            {stopped ? "stopping after the current report..." : run.phase}
            {current ? ` · ${current}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: "#888" }}>
            {run.dryRun > 0 ? `${run.dryRun} built · ` : `${run.sent} sent · `}
            {run.failed} failed · {run.skipped} skipped
          </span>
          {run.manual && (
            <button
              onClick={stop}
              disabled={stopped}
              className="px-3 py-1 rounded text-xs font-medium"
              style={{
                background: stopped ? "#f3f4f6" : "#fef2f2",
                color: stopped ? "#aaa" : "#dc2626",
                border: "none",
                cursor: stopped ? "default" : "pointer",
              }}
            >
              {stopped ? "Stopping" : "Stop run"}
            </button>
          )}
        </div>
      </div>

      <div className="w-full rounded-full" style={{ background: "#eee", height: "6px", overflow: "hidden" }}>
        <div
          style={{
            width: total > 0 ? `${pct}%` : "100%",
            height: "6px",
            background: stopped ? "#ea580c" : TEAL,
            transition: "width 0.4s",
            opacity: total > 0 ? 1 : 0.35,
          }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs" style={{ color: "#aaa" }}>
          {total > 0 ? `${done} of ${total}` : "working..."}
        </span>
        <span className="text-xs" style={{ color: "#aaa" }}>{total > 0 ? `${pct}%` : ""}</span>
      </div>
      {error && <p className="text-xs mt-2" style={{ color: "#dc2626" }}>{error}</p>}
    </div>
  );
}

function TestSendPanel({ token, customers, inProgress, onStarted, open, onToggle }) {
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("selected");
  const [picked, setPicked] = useState([]);
  const [includeUnselected, setIncludeUnselected] = useState(false);
  const [mode, setMode] = useState("send");
  const [confirmText, setConfirmText] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const listed = customers.filter((c) => {
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return c.customer.toLowerCase().includes(needle) || c.displayName.toLowerCase().includes(needle);
  });

  const toggleCustomer = (title) =>
    setPicked((prev) => (prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]));

  const dry = mode === "dry";
  const live = mode === "live";

  // Which customers a live send would actually mail, so the warning can be
  // specific rather than vague.
  const targeted = live
    ? (scope === "all" ? customers : customers.filter((c) => picked.includes(c.customer)))
      .filter((c) => c.reports.some((r) => r.selected && r.configured))
    : [];
  const reportCount = targeted.reduce(
    (total, c) => total + c.reports.filter((r) => r.selected && r.configured).length,
    0,
  );

  // A dry run mails nobody, so it does not need a test address. A live send
  // needs the confirmation phrase instead.
  const hasTarget = scope === "all" || picked.length > 0;
  const canSend =
    !busy &&
    !inProgress &&
    hasTarget &&
    (live ? confirmText.trim() === FORCE_SEND_PHRASE && targeted.length > 0 : dry || Boolean(email.trim()));

  const send = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(live ? "/admin/csv-reports/force-send" : "/admin/csv-reports/test", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(
          live
            ? { confirm: confirmText.trim(), customers: scope === "all" ? [] : picked }
            : {
                email: email.trim(),
                customers: scope === "all" ? [] : picked,
                includeUnselected,
                dryRun: dry,
              },
        ),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);

      setMessage({
        ok: true,
        text: live
          ? `Force send started — ${targeted.length} customer(s), ${reportCount} report(s) going to their own recipients.`
          : dry
            ? `Dry run started — ${scope === "all" ? "every customer" : `${picked.length} customer(s)`}, no email will be sent.`
            : `Run started — ${scope === "all" ? "every customer" : `${picked.length} customer(s)`} to ${body.recipients.join(", ")}.`,
      });
      setConfirmText("");
      onStarted();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Run Reports Now"
      subtitle="test to yourself, dry run, or force a live send"
      open={open}
      onToggle={onToggle}
    >
      <div className="p-5 space-y-4">
        <p className="text-xs" style={{ color: "#888" }}>
          Runs the real pipeline — same device queries, same CSVs — ignoring the scheduled time.
          {live
            ? " Force send mails every ENABLED report to each customer's own report_email, exactly as the cron would."
            : dry
              ? " Dry run builds every report and reports what it produced, but sends no email at all."
              : " Every email goes only to the address below; customer recipients are never used."}
        </p>

        <div className="flex items-center gap-0">
          {[
            { id: "send", label: "Test to my address", on: "#e6f7fb", ink: TEAL },
            { id: "dry", label: "Dry run — no email", on: "#eef2ff", ink: "#4f46e5" },
            { id: "live", label: "Force send to customers", on: "#fef2f2", ink: RED },
          ].map((option, i) => (
            <button
              key={option.id}
              onClick={() => { setMode(option.id); setConfirmText(""); setMessage(null); }}
              className="px-3 py-1.5 text-xs font-medium"
              style={{
                background: mode === option.id ? option.on : "white",
                color: mode === option.id ? option.ink : "#888",
                border: "1px solid #e5e7eb",
                borderLeft: i === 0 ? "1px solid #e5e7eb" : "none",
                cursor: "pointer",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!live && (
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={dry ? "not needed for a dry run" : "you@company.com"}
              disabled={dry}
              className="px-3 py-2 rounded text-sm"
              style={{ border: "1px solid #e5e7eb", minWidth: "260px", background: dry ? "#fafafa" : "white" }}
            />
          )}
          <label className="flex items-center gap-1.5 text-xs" style={{ color: "#777" }}>
            <input type="radio" checked={scope === "selected"} onChange={() => setScope("selected")} />
            Selected customers
          </label>
          <label className="flex items-center gap-1.5 text-xs" style={{ color: "#777" }}>
            <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} />
            All customers
          </label>
          {!live && (
            <label className="flex items-center gap-1.5 text-xs" style={{ color: "#777" }}>
              <input type="checkbox" checked={includeUnselected} onChange={(e) => setIncludeUnselected(e.target.checked)} />
              Include reports that are turned off
            </label>
          )}
        </div>

        {live && (
          <div className="p-4 rounded-sm" style={{ background: "#fef2f2", border: `1px solid #fecaca` }}>
            <p className="text-sm font-semibold mb-1" style={{ color: RED }}>
              This sends real reports to real customers.
            </p>
            <p className="text-xs mb-3" style={{ color: "#b91c1c" }}>
              {targeted.length} customer(s) and {reportCount} report(s) will be mailed to their configured report_email
              right now, ignoring the scheduled time. Only reports a customer has enabled are sent; nothing that is
              switched off goes out. Customers still receive their normal scheduled run later today.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Type ${FORCE_SEND_PHRASE} to confirm`}
                className="px-3 py-2 rounded text-sm"
                style={{ border: `1px solid ${confirmText && confirmText.trim() !== FORCE_SEND_PHRASE ? "#fca5a5" : "#e5e7eb"}`, minWidth: "280px" }}
              />
              <span className="text-xs" style={{ color: "#b91c1c" }}>
                {targeted.length === 0 ? "No selected customer has a report enabled." : ""}
              </span>
            </div>
          </div>
        )}

        {scope === "all" && !live && (
          <p className="text-xs" style={{ color: dry ? "#888" : "#c2410c" }}>
            Every customer runs, including ones with no report_email set (hidden from the table below). On a large tenant
            this can take several minutes{dry ? "" : " and produce a lot of mail"} — you can stop it from the progress bar.
          </p>
        )}

        {scope === "selected" && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: "2px" }}>
            <div className="px-3 py-2 flex items-center justify-between gap-3" style={{ borderBottom: "1px solid #f0f0f0" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter customers"
                className="px-2 py-1 rounded text-xs"
                style={{ border: "1px solid #e5e7eb", flex: 1 }}
              />
              <span className="text-xs whitespace-nowrap" style={{ color: "#aaa" }}>{picked.length} selected</span>
              <button onClick={() => setPicked(listed.map((c) => c.customer))} className="text-xs font-medium" style={{ color: TEAL }}>
                All shown
              </button>
              <button onClick={() => setPicked([])} className="text-xs font-medium" style={{ color: "#888" }}>
                Clear
              </button>
            </div>
            <div style={{ maxHeight: "220px", overflowY: "auto" }}>
              {listed.length === 0 && <p className="text-xs p-3" style={{ color: "#aaa" }}>No customers match.</p>}
              {listed.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs"
                  style={{ borderTop: "1px solid #f7f7f7", cursor: "pointer" }}
                >
                  <input type="checkbox" checked={picked.includes(c.customer)} onChange={() => toggleCustomer(c.customer)} />
                  <span style={{ color: DARK }}>{c.displayName}</span>
                  <span style={{ color: "#bbb" }}>
                    {c.reports.filter((r) => r.selected && r.configured).map((r) => REPORT_LABELS[r.type] || r.type).join(", ") || "nothing enabled"}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            onClick={send}
            disabled={!canSend}
            className="px-4 py-2 rounded text-sm font-medium transition-opacity"
            style={{
              background: canSend ? (live ? RED : dry ? "#4f46e5" : TEAL) : "#e5e7eb",
              color: canSend ? "white" : "#aaa",
              border: "none",
              cursor: canSend ? "pointer" : "default",
            }}
          >
            {busy
              ? "Starting..."
              : live
                ? `Force send to ${targeted.length} customer(s)`
                : dry
                  ? "Run without sending"
                  : "Send test reports now"}
          </button>
          {inProgress && <span className="text-xs" style={{ color: "#ea580c" }}>A run is already in progress.</span>}
          {message && (
            <span className="text-xs" style={{ color: message.ok ? TEAL : "#dc2626" }}>{message.text}</span>
          )}
        </div>
      </div>
    </Panel>
  );
}

function CsvReportsTab({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [resultFilter, setResultFilter] = useState("any");
  const [openRun, setOpenRun] = useState(null);
  const [panels, setPanels] = useState({ test: false, schedules: true, runs: false });

  const togglePanel = (key) => setPanels((prev) => ({ ...prev, [key]: !prev[key] }));

  const fetchData = useCallback(async (refresh) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/admin/csv-reports${refresh ? "?refresh=1" : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  const runs = data?.runs || [];
  const activeRun = runs.find((run) => !run.finishedAt) || null;

  // Poll while a run is open so the progress bar actually moves. The schedule
  // scan is served from its own cache, so this stays cheap.
  useEffect(() => {
    if (!activeRun) return undefined;
    const timer = setInterval(() => fetchData(false), 2000);
    return () => clearInterval(timer);
  }, [activeRun, fetchData]);

  const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const formatShort = (iso) =>
    iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const allCustomers = data?.customers || [];

  // Customers with no report_email are skipped by design and are not worth
  // showing - they would swamp the table with rows nobody needs to act on.
  const customers = allCustomers.filter((c) => c.emails.length > 0);
  const hiddenCount = allCustomers.length - customers.length;

  const filtered = customers.filter((c) => {
    if (problemsOnly && c.issues.length === 0) return false;
    if (resultFilter === "none" && c.lastEvent) return false;
    if (resultFilter !== "any" && resultFilter !== "none" && c.lastEvent?.status !== resultFilter) return false;
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return (
      c.customer.toLowerCase().includes(needle) ||
      c.displayName.toLowerCase().includes(needle) ||
      c.emails.join(" ").toLowerCase().includes(needle)
    );
  });

  const active = customers.filter((c) => c.reports.some((r) => r.selected && r.configured)).length;
  const dueNow = customers.filter((c) => c.reports.some((r) => r.dueNow)).length;
  const failing = customers.filter((c) => c.lastEvent?.status === "failed").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold" style={{ color: DARK }}>CSV Report Diagnostics</h2>
          <p className="text-xs mt-0.5" style={{ color: "#aaa" }}>
            {data?.scannedAt ? `Schedule scanned ${formatTime(data.scannedAt)}${data.cached ? " (cached)" : ""}` : "—"}
            {data?.cronExpression ? ` · cron ${data.cronExpression} America/New_York` : ""}
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          className="text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: TEAL }}
        >
          Rescan
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {data?.scheduleError && (
        <p className="text-sm px-4 py-3 rounded-sm" style={{ background: "#fef2f2", color: "#dc2626" }}>
          Could not scan schedules from ThingsBoard: {data.scheduleError}. The run history below is still accurate.
        </p>
      )}

      {data?.forceMode?.enabled && (
        <p className="text-sm px-4 py-3 rounded-sm" style={{ background: "#fff7ed", color: "#c2410c" }}>
          <strong>Debug force mode is ON in csvReportJobs.js.</strong> Every scheduled run is redirected to{" "}
          {data.forceMode.email.join(", ")}
          {data.forceMode.customer
            ? `, and only "${data.forceMode.customer}" is processed.`
            : ", for all customers, ignoring the scheduled times below."}
        </p>
      )}

      {activeRun && <ActiveRunBar run={activeRun} token={token} onChanged={() => fetchData(false)} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Customers" value={customers.length} />
        <StatCard label="Reports Enabled" value={active} />
        <StatCard label="Due This Slot" value={dueNow} />
        <StatCard label="Last Run Failed" value={failing} />
      </div>

      <TestSendPanel
        token={token}
        customers={customers}
        inProgress={Boolean(data?.testRunInProgress)}
        onStarted={() => { setPanels((prev) => ({ ...prev, runs: true })); fetchData(false); }}
        open={panels.test}
        onToggle={() => togglePanel("test")}
      />

      <Panel
        title="Customer Schedules"
        subtitle={hiddenCount > 0 ? `${hiddenCount} without report_email hidden` : null}
        open={panels.schedules}
        onToggle={() => togglePanel("schedules")}
        right={
          <div className="flex items-center gap-3">
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              className="px-2 py-1.5 rounded text-xs"
              style={{ border: "1px solid #e5e7eb", color: resultFilter === "failed" ? "#dc2626" : "#555" }}
            >
              {RESULT_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs" style={{ color: "#777" }}>
              <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
              Config issues
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer or email"
              className="px-3 py-1.5 rounded text-sm"
              style={{ border: "1px solid #e5e7eb", minWidth: "200px" }}
            />
          </div>
        }
      >
        {loading && !data ? (
          <p className="text-sm p-5" style={{ color: "#aaa" }}>Scanning customers...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Customer", "Recipients", "Timezone", "Scheduled Reports", "Last Sent", "Last Result"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: "#888" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm" style={{ color: "#aaa" }}>
                      {customers.length === 0 ? "No customers match." : "No customers match this filter."}
                    </td>
                  </tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.id} style={{ borderTop: "1px solid #f5f5f5", verticalAlign: "top" }}>
                    <td className="px-4 py-3">
                      <div className="text-sm" style={{ color: DARK }}>{c.displayName}</div>
                      {c.displayName !== c.customer && (
                        <div className="text-xs" style={{ color: "#aaa" }}>{c.customer}</div>
                      )}
                      {c.issues.map((issue, i) => (
                        <div key={i} className="text-xs mt-1" style={{ color: "#dc2626" }}>{issue}</div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "#555", maxWidth: "200px" }}>
                      {c.emails.join(", ")}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "#777" }}>
                      {c.timezone}
                      <div style={{ color: "#aaa" }}>slot {c.currentSlot || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1" style={{ maxWidth: "300px" }}>
                        {c.reports.length === 0
                          ? <span className="text-xs" style={{ color: "#aaa" }}>none configured</span>
                          : c.reports.map((r) => <ReportPill key={r.key} report={r} />)}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "#bbb" }}>{c.mode}</div>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "#777" }}>
                      {c.lastSent ? (
                        <>
                          {formatShort(c.lastSent.at)}
                          <div style={{ color: "#aaa" }}>{REPORT_LABELS[c.lastSent.reportType] || c.lastSent.reportType}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ maxWidth: "320px" }}>
                      {c.lastEvent ? (
                        <>
                          <Pill {...statusColors(c.lastEvent.status)}>{c.lastEvent.status}</Pill>
                          {c.lastEvent.reportType && (
                            <span className="ml-2" style={{ color: "#aaa" }}>
                              {REPORT_LABELS[c.lastEvent.reportType] || c.lastEvent.reportType}
                            </span>
                          )}
                          {c.lastEvent.detail && (
                            <div className="mt-1" style={{ color: c.lastEvent.status === "failed" ? "#b91c1c" : "#888" }}>
                              {c.lastEvent.detail}
                            </div>
                          )}
                          <ErrorDetail error={c.lastEvent.error} />
                        </>
                      ) : (
                        <span style={{ color: "#aaa" }}>no run recorded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Recent Runs"
        subtitle={runs.length ? `${runs.length} since last restart` : "none since last restart"}
        open={panels.runs}
        onToggle={() => togglePanel("runs")}
      >
        {runs.length === 0 ? (
          <p className="text-sm p-5" style={{ color: "#aaa" }}>No runs recorded yet — the job runs every 30 minutes.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Started", "Kind", "Duration", "Scanned", "Due", "Sent", "Built", "Failed", "Skipped", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: "#888" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const duration = run.finishedAt
                    ? `${Math.round((new Date(run.finishedAt) - new Date(run.startedAt)) / 1000)}s`
                    : `${run.phase}...`;
                  const isOpen = openRun === run.id;
                  const failures = run.events.filter((e) => e.status === "failed");

                  return [
                    <tr key={run.id} style={{ borderTop: "1px solid #f5f5f5" }}>
                      <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "#555" }}>
                        {formatTime(run.startedAt)}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ maxWidth: "220px" }}>
                        {run.manual ? (
                          <>
                            {run.live
                              ? <Pill bg="#fef2f2" color={RED}>force send</Pill>
                              : <Pill bg="#fff7ed" color="#ea580c">test</Pill>}
                            {run.dryRunMode && <Pill bg="#eef2ff" color="#4f46e5">dry run</Pill>}
                            {run.cancelled && <Pill bg="#f3f4f6" color="#6b7280">stopped</Pill>}
                            <div className="mt-1" style={{ color: "#888" }}>
                              {run.live
                                ? "to each customer's own recipients"
                                : run.dryRunMode
                                  ? "no email sent"
                                  : `to ${(run.forcedEmails || []).join(", ")}`}
                            </div>
                            <div style={{ color: "#bbb" }}>
                              {(run.forcedCustomers || []).length ? run.forcedCustomers.join(", ") : "all customers"}
                              {run.includeUnselected ? " · incl. disabled" : ""}
                            </div>
                          </>
                        ) : run.forced ? (
                          <Pill bg="#fff7ed" color="#ea580c">forced cron</Pill>
                        ) : (
                          <Pill bg="#f3f4f6" color="#6b7280">cron</Pill>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: "#777" }}>{duration}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: "#777" }}>{run.customersScanned}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: "#777" }}>{run.customersDue}</td>
                      <td className="px-4 py-3 text-xs font-semibold" style={{ color: TEAL }}>{run.sent}</td>
                      <td className="px-4 py-3 text-xs font-semibold" style={{ color: run.dryRun ? "#4f46e5" : "#aaa" }}>{run.dryRun || 0}</td>
                      <td className="px-4 py-3 text-xs font-semibold" style={{ color: run.failed ? "#dc2626" : "#aaa" }}>{run.failed}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: "#aaa" }}>{run.skipped}</td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        <button
                          onClick={() => setOpenRun(isOpen ? null : run.id)}
                          className="font-medium transition-opacity hover:opacity-70"
                          style={{ color: failures.length ? "#dc2626" : TEAL }}
                        >
                          {isOpen ? "Hide" : failures.length ? `${failures.length} failure(s)` : `Details (${run.events.length})`}
                        </button>
                      </td>
                    </tr>,
                    isOpen ? (
                      <tr key={`${run.id}-detail`}>
                        <td colSpan={10} style={{ background: "#fafafa", padding: 0 }}>
                          <div style={{ maxHeight: "420px", overflowY: "auto" }}>
                            <table className="w-full text-xs">
                              <tbody>
                                {run.events.map((e, i) => (
                                  <tr key={i} style={{ borderTop: "1px solid #f0f0f0", verticalAlign: "top" }}>
                                    <td className="px-4 py-2 whitespace-nowrap" style={{ color: "#aaa", width: "150px" }}>
                                      {formatShort(e.at)}
                                    </td>
                                    <td className="px-4 py-2" style={{ color: DARK }}>{e.customer}</td>
                                    <td className="px-4 py-2" style={{ color: "#777", width: "120px" }}>
                                      {e.reportType ? (REPORT_LABELS[e.reportType] || e.reportType) : "—"}
                                    </td>
                                    <td className="px-4 py-2" style={{ width: "90px" }}>
                                      <Pill {...statusColors(e.status)}>{e.status}</Pill>
                                    </td>
                                    <td className="px-4 py-2" style={{ color: e.status === "failed" ? "#b91c1c" : "#888" }}>
                                      {[e.emails ? e.emails.join(", ") : "", e.detail || ""].filter(Boolean).join(" · ")}
                                      <ErrorDetail error={e.error} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "bpx-files", label: "BPX Files" },
  { id: "plains-email", label: "Plains Email" },
  { id: "csv-reports", label: "CSV Reports" },
];

export default function Dashboard({ token, email, onLogout }) {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#f4f4f4", overflow: "hidden" }}>
      {/* Header */}
      <div className="bg-white px-6 py-3 flex items-center justify-between" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)", flexShrink: 0 }}>
        <div className="flex items-center gap-4">
          <img
            src="https://dc3control.com/wp-content/uploads/2024/07/New-Logo-for-Mariya-1-1024x492.png"
            alt="DC3 Control"
            className="h-9 object-contain"
          />
          <span className="text-sm font-semibold" style={{ color: TEAL, letterSpacing: "0.04em" }}>DC3 API</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="text-sm" style={{ color: "#888" }}>{email}</span>
          <button
            onClick={onLogout}
            className="text-sm font-medium transition-opacity hover:opacity-70"
            style={{ color: "#dc2626" }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white px-6" style={{ borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
        <div className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === tab.id ? TEAL : "#888",
                borderBottom: activeTab === tab.id ? `2px solid ${TEAL}` : "2px solid transparent",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "2rem 1.5rem" }}>
        <div style={{ maxWidth: "80rem", margin: "0 auto" }}>
          {activeTab === "overview" && <OverviewTab token={token} onLogout={onLogout} />}
          {activeTab === "bpx-files" && <BpxFilesTab token={token} onLogout={onLogout} />}
          {activeTab === "plains-email" && <PlainsEmailTab token={token} onLogout={onLogout} />}
          {activeTab === "csv-reports" && <CsvReportsTab token={token} onLogout={onLogout} />}
        </div>
      </div>
    </div>
  );
}
